import { useEffect, useState, ReactNode } from "react";
import { isNative } from "@/lib/capacitor";

export function OfflineGate({ children }: { children: ReactNode }) {
  const [online, setOnline] = useState(() => {
    if (typeof navigator !== "undefined" && typeof navigator.onLine === "boolean") {
      return navigator.onLine;
    }
    return true;
  });

  useEffect(() => {
    let cancelled = false;
    let cleanup: (() => Promise<void> | void) | undefined;

    (async () => {
      if (!isNative) {
        const update = () => setOnline(navigator.onLine);
        update();
        window.addEventListener("online", update);
        window.addEventListener("offline", update);
        cleanup = () => {
          window.removeEventListener("online", update);
          window.removeEventListener("offline", update);
        };
        return;
      }
      const { Network } = await import("@capacitor/network");
      let handle: Awaited<ReturnType<typeof Network.addListener>> | undefined;
      // Assign cleanup BEFORE awaiting addListener so a fast unmount removes whatever is registered.
      cleanup = async () => {
        try { await handle?.remove(); } catch { /* ignore */ }
      };
      const status = await Network.getStatus();
      if (!cancelled) setOnline(status.connected);
      handle = await Network.addListener("networkStatusChange", (s) => {
        if (!cancelled) setOnline(s.connected);
      });
    })();

    return () => {
      cancelled = true;
      void cleanup?.();
    };
  }, []);

  if (!online) {
    return (
      <div style={{
        minHeight: "100vh",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "calc(env(safe-area-inset-top) + 24px) 24px calc(env(safe-area-inset-bottom) + 24px)",
        background: "hsl(var(--paper))", color: "hsl(var(--ink))",
        textAlign: "center",
      }}>
        <div>
          <h1 style={{ fontSize: 22, marginBottom: 8 }}>You're offline</h1>
          <p>Check your connection and try again.</p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
